import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';

import { api } from '../services/api';
import { fieldSectionSx } from './deviceConfigStyles';

const ON_OFF_OPTIONS = [
  { value: '0', label: 'Off' },
  { value: '1', label: 'On' },
];

const NAME_PATTERN = /^[A-Za-z0-9_.-]+$/;

const blankZone = () => ({
  name: '',
  masq: '0',
});

const zoneEqual = (a, b) => a.name === b.name && a.masq === b.masq;

const zonesArrayEqual = (a, b) => {
  if (a.length !== b.length) return false;
  return a.every((zone, idx) => zoneEqual(zone, b[idx]));
};

const validateZone = (zone, others) => {
  const errors = {};
  const name = (zone.name || '').trim();
  if (!name) errors.name = 'Zone name is required';
  else if (!NAME_PATTERN.test(name)) errors.name = 'Allowed chars: letters, digits, _ . -';
  else if (others.some((z) => z.name.toLowerCase() === name.toLowerCase())) {
    errors.name = 'A zone with this name already exists';
  }
  if (!['0', '1'].includes(String(zone.masq || ''))) errors.masq = 'Pick Off or On';
  return errors;
};

const FirewallSection = ({ serial, isJnet2 }) => {
  const [zones, setZones] = useState([]);
  const [originalZones, setOriginalZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [draft, setDraft] = useState(blankZone());
  const [draftErrors, setDraftErrors] = useState({});

  const fetchZones = useCallback(async () => {
    if (!serial || !isJnet2) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getFirewallConfig(serial);
      const list = Array.isArray(data?.firewall?.zones) ? data.firewall.zones : [];
      setZones(list);
      setOriginalZones(list);
    } catch (err) {
      setError(err?.message || 'Failed to load firewall configuration');
    } finally {
      setLoading(false);
    }
  }, [serial, isJnet2]);

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  const dirty = useMemo(() => !zonesArrayEqual(zones, originalZones), [zones, originalZones]);

  const openAdd = () => {
    setEditIndex(-1);
    setDraft(blankZone());
    setDraftErrors({});
    setDialogOpen(true);
  };

  const openEdit = (idx) => {
    setEditIndex(idx);
    setDraft({ ...blankZone(), ...zones[idx] });
    setDraftErrors({});
    setDialogOpen(true);
  };

  const handleDelete = (idx) => {
    setZones((prev) => prev.filter((_, i) => i !== idx));
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDraftErrors({});
  };

  const handleDraftChange = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    if (draftErrors[field]) {
      setDraftErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleDialogSave = () => {
    const others = editIndex >= 0 ? zones.filter((_, i) => i !== editIndex) : zones;
    const errs = validateZone(draft, others);
    if (Object.keys(errs).length > 0) {
      setDraftErrors(errs);
      return;
    }
    const cleaned = {
      name: draft.name.trim(),
      masq: String(draft.masq || '0'),
    };
    if (editIndex >= 0) {
      setZones((prev) => prev.map((z, i) => (i === editIndex ? cleaned : z)));
    } else {
      setZones((prev) => [...prev, cleaned]);
    }
    setDialogOpen(false);
  };

  const handleSaveAll = async () => {
    if (!serial) return;
    setSaving(true);
    setStatus(null);
    try {
      const response = await api.putFirewallConfig(serial, zones);
      const newZones = Array.isArray(response?.view?.firewall?.zones)
        ? response.view.firewall.zones
        : zones;
      setZones(newZones);
      setOriginalZones(newZones);
      setStatus({ type: 'success', message: 'Firewall configuration pushed to the device.' });
    } catch (err) {
      setStatus({ type: 'error', message: err?.message || 'Failed to update firewall configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setZones(originalZones);
    setStatus(null);
  };

  if (!isJnet2) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={fieldSectionSx}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="subtitle1">Firewall</Typography>
          <Typography variant="body2" color="text.secondary">
            Optional top-level firewall zones (for example masquerading on LAN). Clearing all
            zones removes the firewall section from the device config.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh from device config">
            <span>
              <IconButton size="small" onClick={fetchZones} disabled={loading || saving}>
                {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openAdd} disabled={loading || saving}>
            Add Zone
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={fetchZones}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {status && (
        <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus(null)}>
          {status.message}
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Zone name</TableCell>
              <TableCell>Masquerade (masq)</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {zones.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                    No firewall zones configured.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {zones.map((zone, idx) => (
              <TableRow key={`${zone.name}-${idx}`} hover>
                <TableCell sx={{ fontFamily: 'monospace' }}>{zone.name}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={zone.masq === '1' ? 'On' : 'Off'}
                    color={zone.masq === '1' ? 'success' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(idx)} disabled={saving}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(idx)} disabled={saving}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RestoreIcon />}
          onClick={handleReset}
          disabled={!dirty || saving}
        >
          Discard Changes
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          onClick={handleSaveAll}
          disabled={!dirty || saving}
        >
          {saving ? 'Saving…' : 'Save Firewall'}
        </Button>
      </Stack>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{editIndex >= 0 ? 'Edit Firewall Zone' : 'New Firewall Zone'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Zone name"
              value={draft.name}
              onChange={(e) => handleDraftChange('name', e.target.value)}
              fullWidth
              size="small"
              placeholder="lan"
              error={Boolean(draftErrors.name)}
              helperText={draftErrors.name || 'e.g. lan, wan'}
            />
            <FormControl size="small" fullWidth error={Boolean(draftErrors.masq)}>
              <InputLabel>Masquerade (masq)</InputLabel>
              <Select
                label="Masquerade (masq)"
                value={draft.masq || '0'}
                onChange={(e) => handleDraftChange('masq', e.target.value)}
              >
                {ON_OFF_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleDialogSave}>
            {editIndex >= 0 ? 'Update Zone' : 'Add Zone'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FirewallSection;
